import axios from "axios";
import { getAccessToken, refreshToken } from "./keycloakService";
import { jwtRefreshToken } from "./jwtAuthService";
import { getEnv, isDevelopment, isDebugMode } from "../envConfig";
import localStorageService from "./localStorageService";
import { isTokenExpired } from "../utils/tokenUtils";

const env = getEnv();
const AUTH_METHOD = env.AUTH_METHOD || "keycloak";

const axiosInstance = axios.create({
  baseURL: env.ENDPOINT || "",
  timeout: 30000, // 30 seconds timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor: attach access token and add request logging
axiosInstance.interceptors.request.use(
  async (config) => {
    // Skip auth for anonymous login endpoint
    if (config.url?.includes('/anonymous-login')) {
      console.log('🔓 Skipping auth for anonymous login');
      return config;
    }

    // Get the current access token
    let token = null;

    try {
      token = await getAccessToken();
    } catch (error) {
      console.warn("Failed to get token from keycloak service:", error);
    }

    if (!token) {
      token = localStorageService.getItem("access_token");
    }

    // Proactively refresh if the token is already expired so the request
    // never reaches the server with a stale token (which would cause a
    // server-side Keycloak redirect → CORS ERR_FAILED for anonymous users).
    if (token && isTokenExpired(token) && !config._proactiveRefresh) {
      config._proactiveRefresh = true;
      try {
        const newTokens = AUTH_METHOD === "jwt"
          ? await jwtRefreshToken()
          : await refreshToken();
        token = newTokens?.accessToken ?? newTokens;
        console.log("🔄 Token proactively refreshed before request.");
      } catch (refreshErr) {
        console.warn("⚠️ Proactive token refresh failed:", refreshErr.message);
        token = null;
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;

      // Log token for debugging (only in development)
      if (isDevelopment() && isDebugMode()) {
        console.log("🔑 Token attached:", token.substring(0, 20) + "...");
      }
    } else {
      console.warn("⚠️ No access token found");
    }

    // Log request for debugging (only in development and debug mode)
    if (isDevelopment() && isDebugMode()) {
      console.log(
        `🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`,
        {
          params: config.params,
          data: config.data,
          headers: {
            ...config.headers,
            Authorization: config.headers.Authorization
              ? "Bearer [HIDDEN]"
              : "None",
          },
        }
      );
    }

    return config;
  },
  (error) => {
    console.error("❌ Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor: handle 401, refresh token, and add response logging
axiosInstance.interceptors.response.use(
  (response) => {
    // Log successful response for debugging (only in development and debug mode)
    if (isDevelopment() && isDebugMode()) {
      console.log(
        `✅ API Response: ${response.status} ${response.config.url}`,
        {
          data: response.data,
          headers: response.headers,
        }
      );
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Skip token refresh for anonymous login endpoint
    if (originalRequest?.url?.includes('/anonymous-login')) {
      console.log('🔓 Skipping token refresh for anonymous login error');
      return Promise.reject(error);
    }

    // Log error for debugging (always log errors)
    console.error("❌ API Error:", {
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      message: error.message,
      data: error.response?.data,
    });

    // Handle 401 Unauthorized - try to refresh token
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        // Both services now return { accessToken, refreshToken }
        let newTokens;
        if (AUTH_METHOD === "jwt") {
          newTokens = await jwtRefreshToken();
        } else {
          newTokens = await refreshToken();
        }

        // Support both new object shape and legacy plain-string return
        const newAccessToken = newTokens?.accessToken ?? newTokens;

        if (newAccessToken) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        console.error("❌ Token refresh failed:", refreshError);

        // If refresh token is expired, logout and redirect to login
        if (refreshError.message === "REFRESH_TOKEN_EXPIRED") {
          // Clear all tokens
          await localStorageService.removeItem("access_token");
          await localStorageService.removeItem("refresh_token");
          await localStorageService.removeItem("user");

          // Redirect to login page
          window.location.href = "/login";

          return Promise.reject(
            new Error("Session expired. Please login again.")
          );
        }

        return Promise.reject(refreshError);
      }
    }

    // Handle other common HTTP errors
    if (error.response) {
      switch (error.response.status) {
        case 403:
          console.error("🚫 Access forbidden - insufficient permissions");
          break;
        case 404:
          console.error("🔍 Resource not found");
          break;
        case 422:
          console.error("⚠️ Validation error:", error.response.data);
          break;
        case 500:
          console.error("💥 Server error - please try again later");
          break;
        default:
          console.error("❓ Unexpected error occurred");
      }
    } else if (error.request) {
      console.error("🌐 Network error - please check your connection");
    } else {
      console.error("⚙️ Request configuration error:", error.message);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
